import React, { useLayoutEffect, useRef, useState } from 'react';

import { OVERLAY_Z_INDEX } from './constants';

interface SpotlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface SpotlightOverlayProps {
  anchorEls: HTMLElement[];
  padding?: number;
  borderRadius?: number;
}

const OVERLAY_STYLE: React.CSSProperties = {
  zIndex: OVERLAY_Z_INDEX,
};

function measureRects(elements: HTMLElement[], padding: number): SpotlightRect[] {
  const rects: SpotlightRect[] = [];
  for (const el of elements) {
    const r = el.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) continue;
    rects.push({
      top: r.top - padding,
      left: r.left - padding,
      width: r.width + padding * 2,
      height: r.height + padding * 2,
    });
  }
  return rects;
}

function buildRoundedRect(x: number, y: number, w: number, h: number, r: number): string {
  const cr = Math.min(r, w / 2, h / 2);
  return [
    `M ${x + cr} ${y}`,
    `H ${x + w - cr} Q ${x + w} ${y} ${x + w} ${y + cr}`,
    `V ${y + h - cr} Q ${x + w} ${y + h} ${x + w - cr} ${y + h}`,
    `H ${x + cr} Q ${x} ${y + h} ${x} ${y + h - cr}`,
    `V ${y + cr} Q ${x} ${y} ${x + cr} ${y}`,
    'Z',
  ].join(' ');
}

function buildCutoutPath(rects: SpotlightRect[], borderRadius: number): string {
  const parts = ['M 0 0 H 10000 V 10000 H 0 Z'];
  for (const r of rects) {
    parts.push(buildRoundedRect(r.left, r.top, r.width, r.height, borderRadius));
  }
  return parts.join(' ');
}

const SpotlightOverlay = ({ anchorEls, padding = 0, borderRadius = 0 }: SpotlightOverlayProps) => {
  const [rects, setRects] = useState<SpotlightRect[]>([]);
  const elsRef = useRef(anchorEls);
  elsRef.current = anchorEls;

  useLayoutEffect(() => {
    if (anchorEls.length === 0) {
      setRects([]);
      return;
    }

    const update = () => setRects(measureRects(elsRef.current, padding));
    update();

    const ro = new ResizeObserver(update);
    anchorEls.forEach((el) => ro.observe(el));
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);

    return () => {
      ro.disconnect();
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [anchorEls.length, ...anchorEls, padding]);

  const clipPath =
    rects.length > 0 ? `path(evenodd, "${buildCutoutPath(rects, borderRadius)}")` : undefined;

  return (
    <div
      className="fixed inset-0 bg-white/50"
      style={{ ...OVERLAY_STYLE, clipPath }}
      aria-hidden="true"
    />
  );
};

export default SpotlightOverlay;
